import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import { FaExclamationTriangle } from 'react-icons/fa';
import Tabs from '../components/common/Tabs.jsx';
import VerticalBarChart from '../components/charts/VerticalBarChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import LineChart from '../components/charts/LineChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import { AutoScalingIcon, HealthyIcon, UnhealthyIcon, SavingsIcon } from '../components/icons.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  exportCSV, buildExportFilename, ADD_NEW_ACCOUNT,
  setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';
import MultiSelect from '../components/common/MultiSelect.jsx';

import OptimizationService from '../services/OptimizationService';
import useTagList from '../hooks/useTagList';
import { useAuth } from '../state/AuthContext';

// Tab order: 0 = Combined, 1 = CloudsAnalytics (Custom), 2 = AWS
const tabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'AWS Recommendations' },
];

// ── Helpers ────────────────────────────────────────────────
const getAsgName = (item) =>
  item.AutoScalingGroupName ?? item.autoScalingGroupName ?? item.asgName ?? '—';

const getCapacityLabel = (item) => {
  const min     = item.MinSize         ?? item.minSize         ?? '?';
  const desired = item.DesiredCapacity ?? item.desiredCapacity ?? '?';
  const max     = item.MaxSize         ?? item.maxSize         ?? '?';
  return `${min} / ${desired} / ${max}`;
};

const getInstanceType = (item) => {
  if (item.InstanceMix)         return item.InstanceMix;
  if (item.instanceType)        return item.instanceType;
  if (item.InstanceType)        return item.InstanceType;
  if (item.currentInstanceType) return item.currentInstanceType;
  if (Array.isArray(item.Instances) && item.Instances.length > 0) {
    return item.Instances[0]?.InstanceType ?? item.Instances[0]?.instanceType ?? '—';
  }
  return '—';
};

const getRegion = (item) => item.Region ?? item.region ?? '—';

const resolveASGRec = (apiRecType, finding) => {
  const displayVal = apiRecType || finding || '';
  const t = displayVal.toUpperCase().trim();

  let recPillClass = 'rec-rs';
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
    recPillClass = 'rec-ok';
  } else if (t === 'MIXED_SPOT' || t === 'SPOT' || t === 'BUY' || t === 'RI' || t.includes('SPOT') || t.includes('RI')) {
    recPillClass = 'rec-buy';
  }

  return {
    recType: displayVal,
    recPillClass
  };
};

// ── Row Mapper ─────────────────────────────────────────────
const mapASGItem = (item, source) => {
  const current = parseCost(
    item.CurrentOnDemandMonthlyPriceValue ??
    item.currentOnDemandMonthlyPriceValue ??
    item.currentInstanceMonthlyCostValue
  );

  const recommended = parseCost(
    source === 'custom'
      ? (item.CustomRecommendedOnDemandMonthlyPriceValue ??
         item.customRecommendedOnDemandMonthlyPriceValue ??
         item.recommendedOnDemandMonthlyPriceValue)
      : (item.RecommendedOnDemandMonthlyPriceValue ??
         item.recommendedOnDemandMonthlyPriceValue)
  );

  const apiSaving = item.SavingCost ?? item.savingCost;
  const saving = apiSaving != null
    ? parseCost(apiSaving)
    : (current > recommended && recommended > 0 ? current - recommended : 0);

  const rawRecType = item.RecommendationType ?? item.recommendationType ?? '';
  const { recType, recPillClass } = resolveASGRec(rawRecType, item.finding);

  const rawDetail = item.RecommendationDetail ?? item.recommendationDetail ?? '';
  const detailClean = rawDetail.toLowerCase() === 'na' || rawDetail.toLowerCase() === 'optimal' ? '' : rawDetail;

  const rawRemark = item.customRecomendationRemark ?? item.CustomRecomendationRemark ?? '';
  const remarkClean = rawRemark.toUpperCase().trim() === 'NA' ? '' : rawRemark.trim();

  const recommendationRemark = remarkClean || detailClean;

  const recLabel =
    item.RecommendedinstanceType && item.RecommendedinstanceType !== 'NA'
      ? `-> ${item.RecommendedinstanceType}`
      : item.RecommendeddesiredCapacity
      ? `desired -> ${item.RecommendeddesiredCapacity}`
      : '';

  const instanceErrors = Array.isArray(item.Instances)
    ? item.Instances.filter((inst) => inst.Error || inst.error)
    : [];

  return {
    id:                    getAsgName(item),
    name:                  getAsgName(item),
    region:                getRegion(item),
    capacity:              getCapacityLabel(item),
    instanceType:          getInstanceType(item),
    cost:                  `$${current.toFixed(2)}`,
    costValue:             current,
    recommendedCost:       `$${recommended.toFixed(2)}`,
    recommendedCostValue:  recommended,
    recommendationRemark,
    recType,
    recPillClass,
    recLabel,
    saving:                formatSavingStr(saving),
    savingValue:           saving,
    actionLabel:           'Assist with AI',
    actionClass:           'mini-btn ai-assist',
    checked:               false,
    // Capacity
    desiredCapacity:       item.DesiredCapacity        ?? item.desiredCapacity        ?? 0,
    minSize:               item.MinSize                ?? item.minSize                ?? 0,
    maxSize:               item.MaxSize                ?? item.maxSize                ?? 0,
    // Health
    healthyCount:          item.HealthyInstancesCount  ?? item.healthyInstancesCount  ?? 0,
    // Instance mix — SpotInstanceCount/OnDemandInstanceCount from COMBINED_ASG only
    spotInstanceCount:     item.SpotInstanceCount      ?? 0,
    onDemandInstanceCount: item.OnDemandInstanceCount  ?? 0,
    // Time-series (COMBINED_ASG only — empty arrays for other sources)
    scalingActivityData:    Array.isArray(item.ScalingActivityData)   ? item.ScalingActivityData   : [],
    spotVsOnDemandData:     Array.isArray(item.SpotVsOnDemandData)    ? item.SpotVsOnDemandData    : [],
    scalingEventsByHour:    Array.isArray(item.ScalingEventsByHour)   ? item.ScalingEventsByHour   : [],
    // Instances with API-reported errors
    instanceErrors,
    tags: item.Tags ?? item.tags ?? [],
  };
};

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue, rows) => {
  const totalCount   = rows.length;
  const totalDesired = rows.reduce((s, r) => s + (r.desiredCapacity ?? 0), 0);
  const healthy      = totalPriceValue?.totalHealthyInstancesCount   ?? totalDesired;
  const unhealthy    = totalPriceValue?.totalUnHealthyInstancesCount ?? 0;
  const saving       = parseCost(totalPriceValue?.savingCost ?? 0);
  const totalCost    = rows.reduce((s, r) => s + (r.costValue ?? 0), 0);
  const savingPct    = totalCost > 0 ? ((saving / totalCost) * 100).toFixed(1) : 0;
  const totalInst    = healthy + unhealthy;
  const healthyPct   = totalInst > 0 ? ((healthy / totalInst) * 100).toFixed(0) : 100;
  return [
    { id: 1, title: 'Total ASGs',           value: `${totalCount}`,         subtitle: `${totalDesired} active instances`,  type: 't1' },
    { id: 2, title: 'Total Healthy',         value: `${healthy}`,            subtitle: `${healthyPct}% healthy now`,         type: 't2' },
    { id: 3, title: 'Total Unhealthy (30d)', value: `${unhealthy}`,          subtitle: 'Auto-replaced · investigate',         type: 't3' },
    { id: 4, title: 'Recommended Savings',   value: `$${saving.toFixed(2)}`, subtitle: `${savingPct}% potential saving`,      type: 't4' },
  ];
};

// ── Chart Builders ─────────────────────────────────────────

// Chart 1: Capacity headroom — uses min/desired/max from every source
const buildCapacityData = (rows) => {
  const top = rows
    .filter((r) => (r.desiredCapacity ?? 0) > 0 || (r.maxSize ?? 0) > 0)
    .sort((a, b) => (b.desiredCapacity ?? 0) - (a.desiredCapacity ?? 0))
    .slice(0, 9);
  if (!top.length) return null;
  return {
    labels: top.map((r) => r.name.length > 14 ? r.name.slice(0, 12) + '...' : r.name),
    datasets: [
      { label: 'Min',      data: top.map((r) => r.minSize ?? 0),                                           backgroundColor: '#2ea66f', borderRadius: 4, stack: 's1', barPercentage: 0.7 },
      { label: 'Desired',  data: top.map((r) => r.desiredCapacity ?? 0),                                   backgroundColor: '#f57c00', borderRadius: 4, stack: 's1', barPercentage: 0.7 },
      { label: 'Headroom', data: top.map((r) => Math.max((r.maxSize ?? 0) - (r.desiredCapacity ?? 0), 0)), backgroundColor: '#2196f3', borderRadius: 4, stack: 's1', barPercentage: 0.7 },
    ],
  };
};

// Chart 2: Scaling activity — COMBINED_ASG only (ScalingActivityData per row)
const buildScalingTrendData = (rows) => {
  const dateMap = {};
  rows.forEach((r) => {
    (r.scalingActivityData ?? []).forEach((d) => {
      const key = d.Date ?? d.date;
      if (!key) return;
      if (!dateMap[key]) dateMap[key] = { instances: 0, rps: 0 };
      dateMap[key].instances += (d.Instances      ?? d.instances      ?? 0);
      dateMap[key].rps       += (d.RequestsPerSec ?? d.requestsPerSec ?? 0);
    });
  });
  const dates = Object.keys(dateMap).sort();
  if (!dates.length) return null;
  return {
    labels: dates,
    datasets: [
      {
        label: 'Active instances',
        data: dates.map((d) => dateMap[d].instances),
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245,124,0,0.12)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        yAxisID: 'y1',
      },
      {
        label: 'Requests / sec',
        data: dates.map((d) => dateMap[d].rps),
        borderColor: '#2196f3',
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        borderDash: [4, 3],
        yAxisID: 'y2',
      },
    ],
  };
};

// Chart 3: Spot vs On-Demand
// Source priority:
//   1. SpotVsOnDemandData[].{Type,Count}  — explicit typed array from COMBINED_ASG
//   2. SpotInstanceCount / OnDemandInstanceCount — flat counts from COMBINED_ASG
// Percentages are NOT used: they are % values but the donut shows raw numbers,
// which would make the tooltip display e.g. "75" meaning 75%, misleading users.
// If neither source has real counts → null → "No data available".
const buildSpotOdData = (rows) => {
  // Priority 1: SpotVsOnDemandData typed array
  let spotFromArr = 0, odFromArr = 0, hasArr = false;
  rows.forEach((r) => {
    (r.spotVsOnDemandData ?? []).forEach((d) => {
      hasArr = true;
      const t = (d.Type ?? d.type ?? '').toUpperCase();
      if (t === 'SPOT') spotFromArr += (d.Count ?? d.count ?? 0);
      else              odFromArr   += (d.Count ?? d.count ?? 0);
    });
  });
  if (hasArr && (spotFromArr > 0 || odFromArr > 0)) {
    return {
      labels: ['On-Demand', 'Spot'],
      datasets: [{ data: [odFromArr, spotFromArr], backgroundColor: ['#f57c00', '#2ea66f'], borderWidth: 2, borderColor: '#fff' }],
    };
  }

  // Priority 2: flat instance counts (SpotInstanceCount / OnDemandInstanceCount)
  const totalSpot = rows.reduce((s, r) => s + (r.spotInstanceCount ?? 0), 0);
  const totalOD   = rows.reduce((s, r) => s + (r.onDemandInstanceCount ?? 0), 0);
  if (totalSpot > 0 || totalOD > 0) {
    return {
      labels: ['On-Demand', 'Spot'],
      datasets: [{ data: [totalOD, totalSpot], backgroundColor: ['#f57c00', '#2ea66f'], borderWidth: 2, borderColor: '#fff' }],
    };
  }

  return null;
};

// Chart 4: Scaling events by hour — aggregated from ScalingEventsByHour per row
const buildScaleEventsData = (rows) => {
  const hourMap = {};
  rows.forEach((r) => {
    (r.scalingEventsByHour ?? []).forEach((d) => {
      const hour = d.Hour ?? d.hour;
      if (hour == null) return;
      if (!hourMap[hour]) hourMap[hour] = { scaleOut: 0, scaleIn: 0 };
      hourMap[hour].scaleOut += d.ScaleOut ?? d.scaleOut ?? 0;
      hourMap[hour].scaleIn  += d.ScaleIn  ?? d.scaleIn  ?? 0;
    });
  });
  const hours = Object.keys(hourMap).map(Number).sort((a, b) => a - b);
  if (!hours.length) return null;
  const hasData = hours.some((h) => hourMap[h].scaleOut > 0 || hourMap[h].scaleIn > 0);
  if (!hasData) return null;
  return {
    labels: hours.map(String),
    datasets: [
      { label: 'Scale-out', data: hours.map((h) => hourMap[h].scaleOut), backgroundColor: '#2ea66f', borderRadius: 4, barPercentage: 0.7 },
      { label: 'Scale-in',  data: hours.map((h) => hourMap[h].scaleIn),  backgroundColor: '#f57c00', borderRadius: 4, barPercentage: 0.7 },
    ],
  };
};

const buildInsightActions = (rows) => {
  const seen = new Set();
  const withSaving = rows
    .filter((r) => {
      const key = `${r.name}|${r.recType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return parseSaving(r.saving) > 0;
    })
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => ({
    id: i + 1,
    title: r.name,
    amount: r.saving,
    desc: [r.instanceType, r.recLabel, r.recommendationRemark].filter(Boolean).join(' · '),
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
    row: r,
  }));
  return { actions, totalSaving };
};

// ── Instance Error Modal ───────────────────────────────────
// rows: all ASG rows that have instanceErrors (from the active tab)
const InstanceErrorModal = ({ rows, onClose }) => {
  if (!rows?.length) return null;

  // Flatten: each entry is { asgName, inst }
  const flat = rows.flatMap((r) =>
    (r.instanceErrors ?? []).map((inst) => ({ asgName: r.name, inst }))
  );
  const totalASGs = rows.length;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--surface, #fff)', borderRadius: '12px', padding: '24px', maxWidth: '960px', width: '94%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaExclamationTriangle style={{ fontSize: '20px', color: '#f57c00' }} />
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Instance Errors</h3>
            </div>
            <div style={{ color: 'var(--muted, #888)', fontSize: '13px', marginTop: '4px', paddingLeft: '28px' }}>
              {flat.length} instance{flat.length !== 1 ? 's' : ''} with errors across {totalASGs} ASG{totalASGs !== 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--muted, #888)', lineHeight: 1, padding: '4px 8px', borderRadius: '6px' }}
          >
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--surface-alt, #f8f9fc)', borderBottom: '2px solid var(--line, #e2e6ef)', position: 'sticky', top: 0 }}>
                {['ASG Name', 'Instance ID', 'Type', 'Availability Zone', 'Lifecycle', 'Health Status', 'Error'].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {flat.map(({ asgName, inst }, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--line, #e2e6ef)', background: i % 2 === 0 ? 'transparent' : 'var(--surface-alt, #f8f9fc)' }}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', fontWeight: 500 }}>{asgName}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'nowrap' }}>{inst.InstanceId ?? inst.instanceId ?? '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{inst.InstanceType ?? inst.instanceType ?? '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{inst.AvailabilityZone ?? inst.availabilityZone ?? '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{inst.LifecycleState ?? inst.lifecycleState ?? '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{inst.HealthStatus ?? inst.healthStatus ?? '—'}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--red, #e53935)', wordBreak: 'break-word', maxWidth: '300px' }}>{inst.Error ?? inst.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────
const ASGOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAWS,      setIsLoadingAWS]      = useState(false);

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const handleDetailsClick = (row) => {
    setAiData({
      asgName:              row.name,
      region:               row.region,
      capacity:             row.capacity,
      instanceType:         row.instanceType,
      currentMonthlyCost:   row.cost,
      recommendedCost:      row.recommendedCost,
      recommendation:       row.recType,
      recommendationDetail: row.recommendationRemark,
      estimatedSaving:      row.saving,
    });
    setAiPrompt('You are a cloud cost advisor. Based on this Auto Scaling Group data, give a short, friendly, and natural insight about the cost situation, the recommended action (such as switching to Spot or mixed instances), and the potential monthly savings.');
    setAiOpen(true);
  };

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [awsRows,      setAwsRows]      = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [awsKpi,      setAwsKpi]      = useState([]);

  const { tagList } = useTagList('ASG');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const filteredCustomRows = useMemo(() => {
    if (!filterList.tag || filterList.values.length === 0) return customRows;
    return customRows.filter((row) => {
      const tags = row.tags ?? [];
      return tags.some((t) => (t.Key ?? t.key) === filterList.tag && filterList.values.includes(t.Value ?? t.value));
    });
  }, [customRows, filterList]);

  // Derive active rows and KPIs from current tab — no extra state needed
  const activeRows = useMemo(
    () => activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows,
    [activeTab, combinedRows, filteredCustomRows, awsRows]
  );
  const activeKpi = useMemo(
    () => activeTab === 0 ? combinedKpi : activeTab === 1 ? customKpi : awsKpi,
    [activeTab, combinedKpi, customKpi, awsKpi]
  );
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAWS;

  // ASGs that have at least one instance with an error — drives the KPI card warning badge
  const rowsWithErrors = useMemo(
    () => activeRows.filter((r) => r.instanceErrors?.length > 0),
    [activeRows]
  );

  // Chart data — computed synchronously from activeRows, no flash between tab switches
  const capacityData     = useMemo(() => buildCapacityData(activeRows),      [activeRows]);
  const scalingTrendData = useMemo(() => buildScalingTrendData(activeRows),  [activeRows]);
  const spotOdData       = useMemo(() => buildSpotOdData(activeRows),        [activeRows]);
  const scaleEventsData  = useMemo(() => buildScaleEventsData(activeRows),   [activeRows]);
  const { actions: insightActions, totalSaving: insightTotal } = useMemo(
    () => buildInsightActions(activeRows),
    [activeRows]
  );

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Auto Scaling Group Optimization',
      kpi: activeKpi,
      asgRecommendations: activeRows,
    });
  }, [activeRows, activeKpi]);

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: Array.from(value) }));
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    if (tabIndex === 0) {
      // Combined
      setIsLoadingCombined(true);
      setCombinedRows([]);
      OptimizationService.getTabularData(selectedAccount, 'COMBINED_ASG', null, isForceRefresh)
        .then((data) => {
          const mapped = (data?.rows ?? []).map((item) => mapASGItem(item, 'combined'));
          setCombinedRows(mapped);
          setCombinedKpi(buildKpiData(data?.totalPriceValue, mapped));
        })
        .finally(() => setIsLoadingCombined(false));
    } else if (tabIndex === 1) {
      // CloudsAnalytics
      setIsLoadingCustom(true);
      setCustomRows([]);
      OptimizationService.getTabularData(selectedAccount, 'CUSTOM_ASG', null, isForceRefresh)
        .then((data) => {
          const mapped = (data?.rows ?? []).map((item) => mapASGItem(item, 'custom'));
          setCustomRows(mapped);
          setCustomKpi(buildKpiData(data?.totalPriceValue, mapped));
        })
        .finally(() => setIsLoadingCustom(false));
    } else {
      // AWS
      setIsLoadingAWS(true);
      setAwsRows([]);
      OptimizationService.getTabularData(selectedAccount, 'ASG', null, isForceRefresh)
        .then((data) => {
          const mapped = (data?.rows ?? []).map((item) => mapASGItem(item, 'aws'));
          setAwsRows(mapped);
          setAwsKpi(buildKpiData(data?.totalPriceValue, mapped));
        })
        .finally(() => setIsLoadingAWS(false));
    }
  };

  const handleTabChange = (i) => { setActiveTab(i); fetchTabData(i); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchedTabsRef.current = new Set(); fetchTabData(0); } }, [selectedAccount]);

  const asgColumns = [
    { key: 'name',                 label: 'ASG Name' },
    { key: 'region',               label: 'Region' },
    { key: 'capacity',             label: 'Min / Desired / Max' },
    { key: 'instanceType',         label: 'Instance Type' },
    { key: 'cost',                 label: 'Cost / Mo',     className: 'num', render: (row) => <CostCell row={row} /> },
    { key: 'recommendationRemark', label: 'Recommendation',           render: (row) => <RecCell row={row} /> },
    { key: 'saving',               label: 'Saving / Mo',              className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
    { key: 'action',               label: 'Action',                   render: (row) => <button className={row.actionClass} onClick={() => handleDetailsClick(row)}>{row.actionLabel}</button> },
  ];

  const handleExport = () =>
    exportCSV(activeRows, asgColumns.filter((c) => typeof c.label === 'string' && c.key !== 'action'), buildExportFilename(`asg_optimization_${['combined', 'cloudsanalytics', 'aws'][activeTab] ?? activeTab}`, selectedAccount));

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>&#x203A;</span> <strong>Auto Scaling Group</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Auto Scaling Group Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} groups</span>}
          </h1>
          <div className="meta">Tune capacity, schedules, and instance mix across all Auto Scaling Groups</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={activeRows.length === 0}>&#x2B07; Export</button>
          <button className="btn ghost" onClick={() => fetchTabData(activeTab, true)}>&#x21BB; Refresh</button>
        </div>
      </div>

      <KpiRow
        kpiData={activeKpi}
        loading={isLoadingActive}
        iconOverrides={{ t1: <AutoScalingIcon />, t2: <HealthyIcon />, t3: <UnhealthyIcon />, t4: <SavingsIcon /> }}
        warnings={{ t1: rowsWithErrors.length > 0 ? { count: rowsWithErrors.length, onClick: () => setErrorModalOpen(true) } : undefined }}
      />

      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Capacity headroom" subtitle="Min, desired, max per ASG (top 9)">
          <VerticalBarChart data={capacityData} stacked loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top ASG actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="groups"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>
      
      <Tabs tabs={tabs} defaultActive={0} onTabChange={handleTabChange} />

      {activeTab === 1 && (
        <div className="filter-bar">
          <div className="field">
            <label>Filter Tag</label>
            <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
              <option value="">None</option>
              {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Filter Tag Values</label>
            <MultiSelect
              options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
              value={filterList.values}
              onChange={(vals) => handleFilterChange('values', vals)}
              disabled={!filterList.tag}
            />
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setFilterList({ tag: '', values: [] })}>✕ Clear</button>
          <button className="btn primary" onClick={() => fetchTabData(activeTab, true)}>↻ Refresh</button>
        </div>
      )}

      <Table
        rows={activeRows}
        columns={asgColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No Auto Scaling Groups found."
        subscriptionGated
      />

      {/* Spot vs On-Demand vs Reserved Usage + Scaling Events by Hour — commented out: graph data not yet available */}
      {/* <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Spot vs On-Demand usage" subtitle="Current instance market composition">
          <DonutChart data={spotOdData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Scaling events by hour" subtitle="Scale-out vs scale-in across the day (3h buckets)">
          <VerticalBarChart data={scaleEventsData} grouped loading={isLoadingActive} />
        </Card>
      </div> */}

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />

      {errorModalOpen && (
        <InstanceErrorModal rows={rowsWithErrors} onClose={() => setErrorModalOpen(false)} />
      )}
    </>
  );
};

export default ASGOptimization;
